import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { TooltipProps } from "recharts";
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
} from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { CHART_COLORS } from "./chartColors";

interface RadarChartProps {
  accountName: string;
  competitorNames: string[];
  wrapperStyleLegend?: React.CSSProperties;
  categories: Array<{
    name: string;
    account_score: number;
    competitor_scores: (number | null)[];
  }>;
  height?: number;
  outerRadius?: string;
  onAxisClick?: (categoryName: string) => void;
  showLegend?: boolean;
  tickFontSize?: number;
  activeCategory?: string;
}

// Custom tick for clickable axis labels
interface AxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  textAnchor?: string;
  onAxisClick?: (categoryName: string) => void;
  fontSize?: number;
  activeCategory?: string;
}

function ClickableAxisTick({
  x,
  y,
  payload,
  textAnchor,
  onAxisClick,
  fontSize = 16,
  activeCategory,
}: AxisTickProps) {
  const isClickable = !!onAxisClick;
  const isActive = !!activeCategory && payload?.value === activeCategory;
  const [hovered, setHovered] = useState(false);
  const showHighlight = isActive || (isClickable && hovered);

  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fontSize={fontSize}
      fontWeight={showHighlight ? 700 : 500}
      fill={showHighlight ? "var(--primary)" : "currentColor"}
      style={{
        cursor: isClickable ? "pointer" : "default",
        transition: "fill 0.2s, font-weight 0.2s",
      }}
      onClick={() => payload?.value && onAxisClick?.(payload.value)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {payload?.value}
    </text>
  );
}

// Radar entity visual style — extracted from nested ternaries for readability.
function getRadarEntityStyle(
  isSolo: boolean,
  isHighlighted: boolean,
  isDimmed: boolean,
  isAccount: boolean,
) {
  if (isSolo) return { fillOpacity: 0.18, strokeWidth: 2.5, strokeOpacity: 1, dotRadius: 5 };
  if (isHighlighted) return { fillOpacity: 0.12, strokeWidth: 2.5, strokeOpacity: 1, dotRadius: 5 };
  if (isDimmed) return { fillOpacity: 0, strokeWidth: 0.8, strokeOpacity: 0.5, dotRadius: 2.5 };
  if (isAccount) return { fillOpacity: 0.1, strokeWidth: 1.5, strokeOpacity: 1, dotRadius: 5 };
  return { fillOpacity: 0, strokeWidth: 0.5, strokeOpacity: 1, dotRadius: 2 };
}

// Custom dot that renders the score value as a label when solo (no competitors)
interface ScoreDotProps {
  cx?: number;
  cy?: number;
  value?: number;
  color?: string;
  showLabel?: boolean;
}

function ScoreDot({ cx = 0, cy = 0, value, color = "#000", showLabel = false }: ScoreDotProps) {
  if (value == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={color} fillOpacity={0.9} stroke="var(--card)" strokeWidth={2} />
      {showLabel && (
        <text
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          fontSize={11}
          fontWeight={600}
          fill={color}
        >
          {value.toFixed(1)}
        </text>
      )}
    </g>
  );
}

function computeDomain([dataMin, dataMax]: [number, number]): [number, number] {
  const padding = 0.05;
  const lo = Math.max(0, dataMin * (1 - padding));
  const hi = dataMin === dataMax ? dataMax + 0.5 : dataMax * (1 + padding);
  return [lo, hi];
}

export function RadarChart({
  accountName,
  competitorNames,
  categories,
  height = 300,
  outerRadius = "100%",
  wrapperStyleLegend = {},
  onAxisClick,
  showLegend = true,
  tickFontSize = 16,
  activeCategory,
}: RadarChartProps) {
  if (categories.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground text-sm"
        style={{ height: `${height}px` }}
      >
        No scoring data available
      </div>
    );
  }

  const isSolo = competitorNames.length === 0;

  // Transform data for recharts
  const chartData = useMemo(
    () =>
      categories.map((category) => {
        const dataPoint: Record<string, number | string> = {
          metric: category.name,
        };
        dataPoint[accountName] = category.account_score;
        competitorNames.forEach((competitor, idx) => {
          dataPoint[competitor] = category.competitor_scores[idx] ?? 0;
        });
        return dataPoint;
      }),
    [categories, accountName, competitorNames],
  );

  // All entities: account first, then competitors
  const allEntities = useMemo(
    () => [accountName, ...competitorNames],
    [accountName, competitorNames],
  );

  // Build chart config dynamically
  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    allEntities.forEach((entity, idx) => {
      config[entity] = {
        label: entity,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      };
    });
    return config;
  }, [allEntities]);

  // Highlight state: null = default (account highlighted), string = highlighted entity
  const [highlighted, setHighlighted] = useState<string | null>(null);

  // Reset highlight when the set of entities changes (e.g. peer added/removed).
  // Uses allEntities (memoized) as dependency instead of competitorNames.join(",")
  // anti-pattern that created new string on every render (Audit H3).
  useEffect(() => {
    setHighlighted(null);
  }, [allEntities]);

  const handleLegendClick = (entry: { value?: string; dataKey?: string }) => {
    const name = entry.value ?? entry.dataKey;
    setHighlighted((prev) => (prev === name ? null : name));
  };

  const accountColor = CHART_COLORS[0];

  return (
    <ChartContainer
      config={chartConfig}
      className="w-full"
      style={{ height: `${height}px` }}
    >
      <RechartsRadarChart data={chartData} outerRadius={outerRadius}>
        {showLegend && !isSolo && (
          <Legend
            iconType="plainline"
            wrapperStyle={{ ...wrapperStyleLegend, cursor: "pointer" }}
            verticalAlign="bottom"
            onClick={handleLegendClick}
          />
        )}
        <ChartTooltip
          cursor={false}
          content={(props: TooltipProps<number, string>) => {
            const filtered = highlighted !== null
              ? { ...props, payload: props.payload?.filter((p) => p.dataKey === highlighted) }
              : props;
            return <ChartTooltipContent {...filtered} />;
          }}
        />
        <PolarAngleAxis
          dataKey="metric"
          tick={(props: AxisTickProps) => (
            <ClickableAxisTick {...props} onAxisClick={onAxisClick} fontSize={tickFontSize} activeCategory={activeCategory} />
          )}
        />
        <PolarRadiusAxis
          tickCount={3}
          tick={!isSolo}
          axisLine={false}
          scale="linear"
          angle={45}
          domain={computeDomain}
        />
        <PolarGrid />
        {allEntities.map((entity, idx) => {
          const isAccount = idx === 0;
          const isHighlighted = highlighted === null ? isAccount : entity === highlighted;
          const isDimmed = highlighted !== null && entity !== highlighted;
          const entityColor = CHART_COLORS[idx % CHART_COLORS.length];
          const style = getRadarEntityStyle(isSolo, isHighlighted, isDimmed, isAccount);

          return (
            <Radar
              key={entity}
              dataKey={entity}
              stroke={isSolo ? accountColor : entityColor}
              fill={isSolo ? accountColor : entityColor}
              fillOpacity={style.fillOpacity}
              strokeWidth={style.strokeWidth}
              strokeOpacity={style.strokeOpacity}
              dot={isSolo
                ? (props: ScoreDotProps) => <ScoreDot {...props} color={accountColor} showLabel={true} />
                : {
                    r: style.dotRadius,
                    fillOpacity: isDimmed ? 0.35 : 0.5,
                    fill: entityColor,
                  }
              }
            />
          );
        })}
      </RechartsRadarChart>
    </ChartContainer>
  );
}
